import type { Configuration } from "@azure/msal-browser";

/* global window */

export const TENANT_ID = "e699726b-16bb-4217-876c-79a82e589180";
export const CLIENT_ID = "be4a5c64-1020-432f-91b0-e082158d1a83";

// Use the runtime origin so one bundle works in both dev (localhost:3000)
// and prod (outlook-addin.epic.wongrat.com). Both origins must be
// registered as SPA redirect URIs on the Entra app reg.
export const msalConfig: Configuration = {
  auth: {
    clientId: CLIENT_ID,
    authority: `https://login.microsoftonline.com/${TENANT_ID}`,
    redirectUri: `${window.location.origin}/auth.html`,
  },
  cache: {
    cacheLocation: "localStorage",
    storeAuthStateInCookie: false,
  },
};

// NAA (Nested App Authentication) config. Unlike `msalConfig` — which runs
// inside the auth.html *dialog* — this MSAL instance is created in the task
// pane itself via createNestablePublicClientApplication and brokers token
// requests through the Outlook host. Brokering sidesteps the third-party
// storage partitioning that the dialog flow exists to work around, so silent
// renewal is reliable. Single-tenant authority preserves the wongrat.com
// tenant lock. `redirectUri` targets the task pane page for the brokered
// popup fallback; it AND the `brk-multihub://<origin>` redirect must be
// registered as SPA redirect URIs on the Entra app reg for NAA to work.
export const naaMsalConfig: Configuration = {
  auth: {
    clientId: CLIENT_ID,
    authority: `https://login.microsoftonline.com/${TENANT_ID}`,
    redirectUri: `${window.location.origin}/taskpane.html`,
  },
  cache: {
    cacheLocation: "localStorage",
  },
};

export const epicScopes = [`api://${CLIENT_ID}/access_as_user`];

// Used to fetch Contact headshot thumbnails directly from SharePoint via
// Microsoft Graph. Sites.FullControl.All is the broadest of the SharePoint
// permissions we already have admin-consented; Sites.Read.All would also
// suffice. The Graph token is acquired silently after EPIC login completes.
export const graphScopes = ["https://graph.microsoft.com/Sites.FullControl.All"];

export const EPIC_API_BASE = "https://epic.wongrat.com/api/v1";
export const EPIC_GRAPHQL_URL = "https://epic.wongrat.com/graphql";
export const EPIC_WEB_BASE = "https://epic.wongrat.com";
export const GRAPH_API_BASE = "https://graph.microsoft.com/v1.0";

/**
 * Build the Filament edit URL for a Contact. The Filament panel is mounted
 * at the root (path '') and ContactResource lives inside ContactsCluster,
 * so the path is /contacts/contacts/{id}/edit. Mirrors the named route
 * `filament.admin.contacts.resources.contacts.edit`.
 */
export function contactWebUrl(id: string): string {
  return `${EPIC_WEB_BASE}/contacts/contacts/${encodeURIComponent(id)}/edit`;
}

/**
 * Filament record-page URL templates per UI EntityKind. `{id}` is substituted
 * with the record id. Each entry spells out its own page suffix, because the
 * right page differs per resource: a Filament resource that registers a `view`
 * page serves the record at `/{record}` (bare), while one with only an `edit`
 * page serves it at `/{record}/edit`. Mirrors each resource's getPages() in
 * epic-laravel — check there before adding an entry; guessing the suffix is how
 * you get a 404 that no test catches.
 *
 * Kinds without an entry have no known Filament page yet — the caller
 * should treat the result as "no link available" and hide the affordance.
 */
const ENTITY_URL_TEMPLATE: Record<string, string | undefined> = {
  // Cluster-based resources
  contact: "/contacts/contacts/{id}/edit",
  business: "/businesses/businesses/{id}/edit",
  account: "/accounts/business-accounts/{id}/edit",
  address: "/addresses/addresses/{id}/edit",
  car: "/cars/cars/{id}", // has a view page
  rental: "/travels/car-rentals/{id}/edit",
  tracking: "/courier/tracking-numbers/{id}/edit",
  note: "/communications/notes/{id}/edit",
  trip: "/aviation/trips/{id}/edit",
  correspondence: "/communications/correspondence/{id}/edit",
  phone_call: "/communications/phone-calls/{id}", // Edit page registered at /{record}, no /edit
  sms: "/communications/sms-messages/{id}", // has a view page
  finance_card: "/finances/finance-cards/{id}/edit",
  medical_encounter: "/health/medical-encounters/{id}/edit",
  pnr: "/travels/passenger-name-records/{id}/edit",

  // Root-level resources
  case: "/case-records/{id}/edit",
  task: "/tasks/{id}/edit",
  asset: "/asset-records/{id}/edit",

  // No known Filament page — leave undefined so the caller hides the link:
  //   finance_creditline, aircraft, legal_entity, employment, tag, calendar_event
};

/**
 * Same, for types that reach the UI through the generic "record" catch-all
 * (see entityFromLinkable's default branch). Those all share kind === "record",
 * so ENTITY_URL_TEMPLATE can't tell them apart — key them by GraphQL __typename.
 */
const TYPENAME_URL_TEMPLATE: Record<string, string | undefined> = {
  // CarEvChargingResource registers a view page at /{record}, so no /edit.
  CarEvCharging: "/cars/car-ev-chargings/{id}",
};

/**
 * Build the Filament record URL for an entity, or return null when no Filament
 * page is known for it (caller hides the affordance). `typename` is the raw
 * GraphQL __typename, needed for catch-all "record" entities whose kind alone
 * doesn't identify the type.
 */
export function entityWebUrl(kind: string, id: string, typename?: string): string | null {
  const template =
    ENTITY_URL_TEMPLATE[kind] ?? (typename ? TYPENAME_URL_TEMPLATE[typename] : undefined);
  if (!template) return null;
  return EPIC_WEB_BASE + template.replace("{id}", encodeURIComponent(id));
}
